import { Reminder } from '../types/task';
import { fetchApi } from '../lib/apiClient';
import notificationService from './notification';
import { getPlatformInfo } from '../utils/platformUtils';

/**
 * 提醒服务
 * 用于处理任务提醒的调度和管理
 */
export class ReminderService {
  /**
   * 初始化提醒服务
   * 请求通知权限并注册设备
   */
  static async initialize(): Promise<boolean> {
    try {
      const platformInfo = getPlatformInfo();
      console.log('[ReminderService] Initializing for platform:', platformInfo);

      // 请求通知权限
      const permission = await notificationService.requestPermission();

      if (permission !== 'granted') {
        console.warn('[ReminderService] Notification permission not granted:', permission);

        // 在某些平台上，权限被拒绝后仍可以使用本地通知
        if (platformInfo.isElectron) {
          console.log('[ReminderService] Electron platform detected, continuing with local notifications only');
          return true;
        }

        return false;
      }

      // 注册推送通知（带降级策略）
      try {
        await notificationService.registerForPushNotifications();
        console.log('[ReminderService] Push notifications registered successfully');
        return true;
      } catch (pushError) {
        console.error('[ReminderService] Failed to register push notifications:', pushError);

        // 降级策略：如果推送注册失败，但权限已获得，仍可使用本地通知
        if (permission === 'granted') {
          console.log('[ReminderService] Falling back to local notifications only');
          return true;
        }

        return false;
      }
    } catch (error) {
      console.error('[ReminderService] Failed to initialize reminder service:', error);
      return false;
    }
  }

  /**
   * 调度提醒
   * @param taskId 任务ID
   * @param reminders 提醒列表
   * @deprecated 不再直接调用此方法，提醒调度现在通过任务更新API自动处理
   */
  static async scheduleReminders(taskId: string, reminders: Reminder[]): Promise<boolean> {
    console.warn('[ReminderService] scheduleReminders 方法已废弃，提醒调度现在通过任务更新API自动处理');
    try {
      // 调用后端 API 调度提醒
      const response = await fetchApi('/reminders/schedule', {
        method: 'POST',
        body: {
          taskId,
          reminders
        }
      });

      console.log('[ReminderService] Reminders scheduled successfully:', response);
      return true;
    } catch (error) {
      console.error('[ReminderService] Failed to schedule reminders:', error);
      return false;
    }
  }

  /**
   * 取消提醒
   * @param taskId 任务ID
   * @deprecated 不再直接调用此方法，提醒取消现在通过任务更新API自动处理
   */
  static async cancelReminders(taskId: string): Promise<boolean> {
    console.warn('[ReminderService] cancelReminders 方法已废弃，提醒取消现在通过任务更新API自动处理');
    try {
      // 调用后端 API 取消提醒
      const response = await fetchApi('/reminders/cancel', {
        method: 'POST',
        body: {
          taskId
        }
      });

      console.log('[ReminderService] Reminders cancelled successfully:', response);
      return true;
    } catch (error) {
      console.error('[ReminderService] Failed to cancel reminders:', error);
      return false;
    }
  }

  /**
   * 测试发送通知
   * 用于测试通知功能
   */
  static async testNotification(): Promise<boolean> {
    try {
      // 调用后端 API 发送测试通知
      const response = await fetchApi('/notifications/test', {
        method: 'POST'
      });

      console.log('[ReminderService] Test notification sent successfully:', response);
      return true;
    } catch (error) {
      console.error('[ReminderService] Failed to send test notification:', error);
      return false;
    }
  }
}

// 导出单例
export default ReminderService;
